import React, { AriaAttributes } from 'react'
import { Link } from 'react-router-dom'
import { Action, Fun, IOWidget, lifecycle, mk_widget, mkWidget, nothing, Widget } from 'widgets-for-react'
import { link, linkWrapper } from '../../cms/blocks/utils.gen'
import {
  HeaderAccountMenu,
  HeaderMenuBlocks,
  HeaderMenuItem,
  HeaderMenuLink,
  HeaderMenuLinkList,
  HeaderMenus,
  HeaderState
} from '../../public_site.main.mod'
import { closeAccountMenu, closeMenuItems as closeAllMenuItems } from '../application.mod'
import { DataLayer } from '../utils/datalayer'

const getAriaCurrent = (url: string): AriaAttributes['aria-current'] =>
  window.location.pathname === url ? 'page' : undefined
const closeSubMenus = (s: HeaderState) =>
  s.menu.kind === 'loaded'
    ? {
        ...s.menu,
        value: {
          headerMenuItems: closeAllMenuItems(s.menu.value.headerMenuItems),
          account: closeAccountMenu(s.menu.value.account)
        }
      }
    : s.menu

export const headerDisplay: (props: HeaderState) => Widget<Action<HeaderState>> = props =>
  mk_widget({
    run: setState => {
      const toggleMobileMenu = () => setState(s1 => ({ ...s1, menu: closeSubMenus(s1), open: !s1.open }))
      const closeMobileMenu = () => setState(s1 => ({ ...s1, menu: closeSubMenus(s1), open: false }))
      return (
        <HeaderLayout open={props.open} onToggleMenu={toggleMobileMenu} onCloseMenu={closeMobileMenu}>
          {headerMenu(props).run(setState)}
        </HeaderLayout>
      )
    }
  })

export const header = lifecycle<HeaderState>({
  componentDidMount: setState => window.addEventListener('click', handleClickOutsideMenu(setState)),
  componentWillUnmount: setState => window.removeEventListener('click', handleClickOutsideMenu(setState))
})(headerDisplay)

const lastMenuItemId = 'a11y-last-nav-item'

const HeaderLayout = (props: {
  open: boolean
  onCloseMenu: () => void
  onToggleMenu: () => void
  children: React.ReactNode
}) => {
  const isDesktop = window.innerWidth >= 1024

  React.useEffect(() => {
    // Store/restore last focused element
    const lastFocusedElement = document.querySelector('*:focus')
    const restoreFocus = () => {
      if (lastFocusedElement !== null && lastFocusedElement instanceof HTMLElement) {
        lastFocusedElement.focus()
      }
    }

    // Close entire menu & submenus on Escape + restore focus on last focused element
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        props.onCloseMenu()
        restoreFocus()
      }
    }
    window.addEventListener('keydown', escapeHandler, { passive: true })

    // Mobile: close entire menu after blurring the last menu item + restore focus on last focused element
    const blurHandler = () => {
      props.onCloseMenu()
      restoreFocus()
    }
    const lastMenuItemEL = document.getElementById(lastMenuItemId)
    if (!isDesktop && props.open && lastMenuItemEL) {
      lastMenuItemEL.addEventListener('blur', blurHandler)
    }
    return () => {
      window.removeEventListener('keydown', escapeHandler)
      window.removeEventListener('blur', blurHandler)
    }
  }, [props.open])
  return (
    <>
      <SkipLinks />
      <header className={`header header--main`}>
        <div className="header__inner">
          <div className={`header__logo`}>
            <Link to="/" className="header__logo-link">
              <span className="sr-text">Terug naar bsl.nl</span>
              <img
                src="/frontend/images/logo.svg"
                alt="Logo BSL | Media & Learning"
                width="80"
                height="43"
                loading="eager"
              />
            </Link>
          </div>
          <button
            className={`header__menu-toggle ${props.open ? 'header__menu-toggle-open' : 'header__menu-toggle-closed'}`}
            onClick={props.onToggleMenu}
            aria-controls="a11y-nav"
            aria-expanded={props.open}
            aria-label={`${props.open ? 'Sluit' : 'Open'} het menu`}
            title={`${props.open ? 'Sluit' : 'Open'} het menu`}
            tabIndex={isDesktop ? -1 : undefined}
          >
            <span />
          </button>

          {/* Mobile: <nav> is inert when closed */}
          <nav
            id="a11y-nav"
            aria-label="Primair"
            {...({ inert: !isDesktop && !props.open ? '' : undefined } as any)}
            className={`header__menu ${props.open ? 'header__menu--open' : 'header__menu--closed'}`}
          >
            {props.children}
          </nav>
        </div>
      </header>
    </>
  )
}

const SkipLinks = () => (
  <nav aria-label="Snelkoppelingen">
    <a href="#a11y-main" className="sr-text">
      Ga naar de content
    </a>
    <a href="#a11y-footer" className="sr-text">
      Ga naar de footer
    </a>
  </nav>
)

const handleClickOutsideMenu: Fun<Fun<Action<HeaderState>, void>, Fun<MouseEvent, void>> = setState => e => {
  if (!document.querySelector('.header.header--main').contains(e.target as Node)) {
    setState(s1 => ({
      ...s1,
      menu: closeSubMenus(s1),
      open: false
    }))
  }
}

const headerMenu: IOWidget<HeaderState, Action<HeaderState>> = props =>
  props.menu.kind === 'loaded'
    ? menu(props.menu.value).map(a => s1 => {
        if (s1.menu.kind !== 'loaded') {
          return s1
        }

        return {
          ...s1,
          menu: {
            ...s1.menu,
            value: a(s1.menu.value)
          }
        }
      })
    : nothing()

const menuLink: IOWidget<HeaderMenuLink, Action<HeaderMenuLink>> = item =>
  mk_widget({
    run: setState =>
      link(
        item,
        { classname: 'nav-item__link' },
        <span>{item.title}</span>,
        undefined,
        undefined,
        getAriaCurrent(item.url)
      )
  })

const subMenuId = (title: string) => `a11y-submenu-${title}`
const closeDropdownOnBlur = (title: string, onClose: () => void) => {
  const subMenuEl = document.getElementById(subMenuId(title))
  if (subMenuEl && !subMenuEl.matches(':focus-within')) {
    onClose()
  }
}

const menuBlocks: IOWidget<HeaderMenuBlocks, Action<HeaderMenuBlocks>> = item =>
  mk_widget({
    run: setState => {
      return (
        <>
          <button
            className={`nav-item__button nav-item__button--${item.open ? 'open' : 'closed'}`}
            onClick={() => setState(s1 => ({ ...s1, open: !s1.open }))}
            aria-controls={subMenuId(item.title)}
            aria-expanded={item.open}
          >
            <span>{item.title}</span>
            <svg className="nav-item__icon nav-item__icon--right" width="6" height="8" aria-hidden="true">
              <use xlinkHref="#icon--caret" />
            </svg>
          </button>

          <div
            id={subMenuId(item.title)}
            className={`submenu submenu--grid submenu--${item.open ? 'open' : 'closed'}`}
            {...({ inert: !item.open ? '' : undefined } as any)}
          >
            <div className="submenu-column__titles">
              <p className="title--m submenu-column__title">{item.title}</p>
            </div>
            <ul className={`flex flex--${item.blocks.count()} grid`}>
              {item.blocks.map((block, index) => (
                <li key={`menublock-${index}`} className="flex-item grid-item">
                  {link(
                    block.link,
                    { classname: 'grid-item__link' },
                    <div className="grid-item__content">
                      {block.subtitle && <p className="title--xxs grid-item__subtitle">{block.subtitle}</p>}
                      <p className="title--s grid-item__title">{block.title}</p>
                      <p className="">{block.text}</p>
                    </div>,
                    undefined,
                    () => closeDropdownOnBlur(item.title, () => setState(s1 => ({ ...s1, open: false }))),
                    getAriaCurrent(block.link.url)
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )
    }
  })

const menuLinkList: IOWidget<HeaderMenuLinkList, Action<HeaderMenuLinkList>> = item =>
  mk_widget({
    run: setState => {
      return (
        <>
          <button
            className={`nav-item__button nav-item__button--${item.open ? 'open' : 'closed'}`}
            onClick={() => setState(s1 => ({ ...s1, open: !s1.open }))}
            aria-controls={subMenuId(item.title)}
            aria-expanded={item.open}
          >
            <span>{item.title}</span>
            <svg className="nav-item__icon nav-item__icon--right" width="6" height="8" aria-hidden="true">
              <use xlinkHref="#icon--caret" />
            </svg>
          </button>

          <div
            id={subMenuId(item.title)}
            className={`submenu submenu--link-list submenu--${item.open ? 'open' : 'closed'}`}
            {...({ inert: !item.open ? '' : undefined } as any)}
          >
            {item.list.map((list, index) => {
              return (
                <div key={`submenu-${index}`} className={`submenu-column submenu-column--${index + 1}`}>
                  <div className="submenu-column__titles">
                    <p className="title--m submenu-column__title">{index === 0 ? list.title : 'informatie voor'}</p>
                    {list.text && <p>{list.text}</p>}
                  </div>

                  <ul className="submenu-column__list">
                    {list.links.map((l, key) => (
                      <li key={`submenu-${index}-item-${key}`}>
                        {link(
                          l,
                          { classname: 'submenu-column__link link-button' },
                          l.title,
                          undefined,
                          () => closeDropdownOnBlur(item.title, () => setState(s1 => ({ ...s1, open: false }))),
                          getAriaCurrent(l.url)
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </>
      )
    }
  })

const menu: IOWidget<HeaderMenus, Action<HeaderMenus>> = items =>
  mkWidget<Action<HeaderMenus>>({
    run: setState => (
      <ul className="nav nav--main">
        {items.headerMenuItems
          .map((item, index) => {
            let itemWidget = ((item.kind === 'blocks'
              ? menuBlocks(item)
              : item.kind === 'linkList'
              ? menuLinkList(item)
              : menuLink(item)) as Widget<Action<HeaderMenuItem>>).map<Action<HeaderMenus>>(a => s1 => ({
              ...s1,
              headerMenuItems: closeAllMenuItems(s1.headerMenuItems).set(index, a(item)),
              account: closeAccountMenu(s1.account)
            }))

            return (
              <li key={`main-nav-item-${index}`} className="nav-item">
                {itemWidget.run(setState)}
              </li>
            )
          })
          .valueSeq()}
        {accountMenu(items.account)
          .map<Action<HeaderMenus>>(a => s1 => ({
            ...s1,
            account: a(s1.account),
            headerMenuItems: closeAllMenuItems(s1.headerMenuItems)
          }))
          .run(setState)}
        <li className="nav-item">
          <a
            id={lastMenuItemId}
            href="/shop"
            className="nav-item__link nav-item__link--last nav-item__link--shop"
            onClick={() => DataLayer.clickToShop()}
            aria-current={getAriaCurrent('/shop')}
          >
            <svg className="nav-item__icon nav-item__icon--left" width="10" height="9" aria-hidden="true">
              <use xlinkHref="#icon--cart" />
            </svg>
            <span>naar de shop</span>
          </a>
        </li>
      </ul>
    )
  })

export const accountMenu = (s0: HeaderAccountMenu, action: () => void = () => {}): Widget<Action<HeaderAccountMenu>> =>
  mkWidget({
    run: setState =>
      s0.items.length == 0 ? (
        <li className="nav-item nav-item--right">
          {link(
            linkWrapper({ title: 'BSL account', url: '/mijn-account' }),
            { classname: 'nav-item__link nav-item__link--account' },
            <>
              <svg className="nav-item__icon nav-item__icon--left" width="10" height="10" aria-hidden="true">
                <use xlinkHref="#icon--person" />
              </svg>
              <span>mijn account</span>
            </>,
            () => DataLayer.clickToMyAccount(),
            undefined,
            getAriaCurrent('/mijn-account')
          )}
        </li>
      ) : (
        <li className="nav-item nav-item--right dropdown">
          <button
            className="nav-item__link nav-item__link--account"
            onClick={e => {
              action()
              DataLayer.clickToMyAccount()
              setState(s1 => ({ ...s1, open: !s1.open }))
            }}
            aria-controls="a11y-submenu-account"
            aria-expanded={s0.open}
          >
            <svg className="nav-item__icon nav-item__icon--left" width="10" height="10" aria-hidden="true">
              <use xlinkHref="#icon--person" />
            </svg>
            <span>mijn account</span>
          </button>
          <ul
            id="a11y-submenu-account"
            {...({ inert: !s0.open ? '' : undefined } as any)}
            className={`dropdown__list${s0.open ? '' : ' dropdown__list--hidden'}`}
          >
            {s0.items.map(({ link: item }) => (
              <li className="dropdown__item">
                {link(
                  item,
                  { classname: 'dropdown__link' },
                  <>
                    <svg className="dropdown__icon" width="10" height="10" aria-hidden="true">
                      <use xlinkHref="#icon--person" />
                    </svg>
                    <span className="dropdown__text">{item.title}</span>
                  </>,
                  undefined,
                  () => closeDropdownOnBlur('account', () => setState(s1 => ({ ...s1, open: false }))),
                  getAriaCurrent(item.url)
                )}
              </li>
            ))}
          </ul>
        </li>
      )
  })
